import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { JustificatifRow } from '@/lib/database.types';

export interface JustificatifsFilter {
  entrepriseId?: string | null;
  relatedType?: string | null;
  search?: string;
}

export function useJustificatifs(filter: JustificatifsFilter) {
  return useQuery({
    queryKey: ['justificatifs', filter],
    queryFn: async (): Promise<JustificatifRow[]> => {
      let q = supabase
        .from('justificatifs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.relatedType) q = q.eq('related_type', filter.relatedType);
      if (filter.search && filter.search.length >= 2) {
        q = q.ilike('file_name', `%${filter.search}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export async function computeSha256(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface UploadJustificatifInput {
  entreprise_id: string;
  file: File;
  related_type?: string | null;
  related_id?: string | null;
  notes?: string | null;
}

export function useUploadJustificatif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadJustificatifInput): Promise<JustificatifRow> => {
      const { entreprise_id, file, related_type, related_id, notes } = input;

      if (file.size > 5 * 1024 * 1024) throw new Error('Fichier > 5 MB refusé');
      const sha256 = await computeSha256(file);

      const existing = await supabase
        .from('justificatifs')
        .select('*')
        .eq('entreprise_id', entreprise_id)
        .eq('sha256', sha256)
        .maybeSingle();
      if (existing.data) {
        return existing.data;
      }

      const ext = file.name.split('.').pop() ?? 'bin';
      const path = `${entreprise_id}/${new Date().getFullYear()}/${crypto.randomUUID()}.${ext}`;
      const up = await supabase.storage.from('documents').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });
      if (up.error) throw up.error;

      const { data, error } = await supabase
        .from('justificatifs')
        .insert({
          entreprise_id,
          file_path: path,
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
          sha256,
          related_type: related_type ?? null,
          related_id: related_id ?? null,
          notes: notes ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['justificatifs'] }),
  });
}

export function useBulkDeleteJustificatifs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      rows: JustificatifRow[],
    ): Promise<{ success: string[]; errors: { id: string; message: string }[] }> => {
      const result = { success: [] as string[], errors: [] as { id: string; message: string }[] };
      // Supprime fichiers Storage par batch
      const paths = rows.map((r) => r.file_path);
      if (paths.length > 0) {
        const del = await supabase.storage.from('documents').remove(paths);
        if (del.error) console.warn('storage bulk remove failed:', del.error.message);
      }
      const { error, data } = await supabase
        .from('justificatifs')
        .delete()
        .in(
          'id',
          rows.map((r) => r.id),
        )
        .select('id');
      if (error) {
        for (const r of rows) result.errors.push({ id: r.id, message: error.message });
      } else {
        for (const r of data ?? []) result.success.push(r.id);
      }
      return result;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['justificatifs'] }),
  });
}

export function useDeleteJustificatif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (j: JustificatifRow): Promise<void> => {
      const del = await supabase.storage.from('documents').remove([j.file_path]);
      if (del.error) console.warn('Storage delete failed:', del.error.message);
      const { error } = await supabase.from('justificatifs').delete().eq('id', j.id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['justificatifs'] }),
  });
}

export async function getSignedUrl(path: string, expiresIn = 600): Promise<string | null> {
  const { data, error } = await supabase.storage.from('documents').createSignedUrl(path, expiresIn);
  if (error) {
    console.warn('signed url error', error.message);
    return null;
  }
  return data.signedUrl;
}
